// ============================================================
//  CrystalBuilder — Mini-game 1: Build a number with H/T/O blocks
// ============================================================
import { CS } from '../../constants/Colors.js';
import { PlaceValueQuestion } from '../../learning/math/PlaceValue.js';
import { audio } from '../../audio/AudioManager.js';
import { SaveManager } from '../../state/SaveManager.js';
import { XP_CORRECT, XP_HINT } from '../../learning/math/PlaceValue.js';

export type MiniGameCallback = (success: boolean) => void;

export class CrystalBuilder {
  private scene: Phaser.Scene;
  private container!: Phaser.GameObjects.Container;
  private question: PlaceValueQuestion;
  private onComplete: MiniGameCallback;
  private W: number;
  private H: number;

  private currentH = 0;
  private currentT = 0;
  private currentO = 0;
  private maxH: number;
  private maxT: number;
  private maxO: number;

  private totalDisplay!: Phaser.GameObjects.Text;
  private counterH!: Phaser.GameObjects.Text;
  private counterT!: Phaser.GameObjects.Text;
  private counterO!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private machineGfx!: Phaser.GameObjects.Graphics;
  private energyBarFill!: Phaser.GameObjects.Graphics;
  private attempts = 0;
  private hintUsed = false;

  constructor(scene: Phaser.Scene, question: PlaceValueQuestion, onComplete: MiniGameCallback) {
    this.scene = scene;
    this.question = question;
    this.onComplete = onComplete;
    this.W = scene.scale.width;
    this.H = scene.scale.height;
    this.maxH = question.hundreds;
    this.maxT = question.tens;
    this.maxO = question.ones;
    this.create();
  }

  private create() {
    const { W, H } = this;
    this.container = this.scene.add.container(0, 0);

    // Dark overlay
    const overlay = this.scene.add.graphics();
    overlay.fillStyle(0x000010, 0.88); overlay.fillRect(0, 0, W, H);

    // Panel
    const panel = this.scene.add.graphics();
    panel.fillStyle(0x0d0230, 0.97); panel.fillRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);
    panel.lineStyle(1.5, 0x3d2060, 0.8); panel.strokeRoundedRect(W * 0.06, H * 0.05, W * 0.88, H * 0.9, 20);

    // Title
    const title = this.scene.add.text(W * 0.5, H * 0.1, '⚡ Crystal Energy Builder', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '22px', fontStyle: 'bold',
      color: '#ffd700', stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5, 0.5);

    // Instruction
    const instr = this.scene.add.text(W * 0.5, H * 0.17,
      `Build the number  ${this.question.target}  using hundreds, tens, and ones!`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '15px', color: '#ccddff', align: 'center',
    }).setOrigin(0.5, 0.5);

    // Target number display
    const targetBg = this.scene.add.graphics();
    targetBg.fillStyle(0x1a0a3e, 1); targetBg.fillRoundedRect(W * 0.35, H * 0.22, W * 0.3, H * 0.1, 12);
    targetBg.lineStyle(2, 0x7b2fff, 0.8); targetBg.strokeRoundedRect(W * 0.35, H * 0.22, W * 0.3, H * 0.1, 12);
    const targetLabel = this.scene.add.text(W * 0.5, H * 0.23, 'TARGET', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '11px', color: '#9b4dff',
    }).setOrigin(0.5, 0);
    const targetNum = this.scene.add.text(W * 0.5, H * 0.265, `${this.question.target}`, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '34px', fontStyle: 'bold',
      color: '#ffffff', stroke: '#7b2fff', strokeThickness: 3,
    }).setOrigin(0.5, 0.5);

    // Machine display (shows current total)
    this.machineGfx = this.scene.add.graphics();
    this.drawMachine(W * 0.5, H * 0.38, 0);

    this.totalDisplay = this.scene.add.text(W * 0.5, H * 0.38, '0', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '30px', fontStyle: 'bold', color: '#00e5ff',
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5);

    // Energy progress bar
    const barLabel = this.scene.add.text(W * 0.5, H * 0.46, 'Energy Built', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '11px', color: '#888aaa',
    }).setOrigin(0.5, 0);
    const energyBarBg = this.scene.add.graphics();
    energyBarBg.fillStyle(0x1a0a3e, 1);
    energyBarBg.fillRoundedRect(W * 0.25, H * 0.49, W * 0.5, 14, 6);
    this.energyBarFill = this.scene.add.graphics();

    // Block columns
    this.buildBlockColumns(W, H);

    // Current total breakdown
    const breakdownBg = this.scene.add.graphics();
    breakdownBg.fillStyle(0x100825, 0.9); breakdownBg.fillRoundedRect(W * 0.1, H * 0.72, W * 0.8, 52, 10);

    this.counterH = this.scene.add.text(W * 0.25, H * 0.748, 'Hundreds: 0', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '14px', color: '#c080ff',
    }).setOrigin(0.5, 0.5);
    this.counterT = this.scene.add.text(W * 0.5, H * 0.748, 'Tens: 0', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '14px', color: '#00e5ff',
    }).setOrigin(0.5, 0.5);
    this.counterO = this.scene.add.text(W * 0.75, H * 0.748, 'Ones: 0', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '14px', color: '#ffd700',
    }).setOrigin(0.5, 0.5);

    // Buttons row
    const checkBtn = this.makeButton(W * 0.38, H * 0.84, '✓ Check!', '#00e676', () => this.checkAnswer());
    const clearBtn = this.makeButton(W * 0.62, H * 0.84, '↺ Clear', '#ff8c00', () => this.clearAll());
    const hintBtn  = this.makeButton(W * 0.5,  H * 0.92, '💡 Hint',  '#ffeb3b', () => this.showHint());

    // Feedback
    this.feedbackText = this.scene.add.text(W * 0.5, H * 0.56, '', {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '16px', fontStyle: 'bold', color: '#00e676',
      stroke: '#000', strokeThickness: 2, align: 'center',
    }).setOrigin(0.5, 0.5);

    this.container.add([
      overlay, panel, title, instr,
      targetBg, targetLabel, targetNum,
      this.machineGfx, this.totalDisplay,
      barLabel, energyBarBg, this.energyBarFill,
      breakdownBg, this.counterH, this.counterT, this.counterO,
      checkBtn, clearBtn, hintBtn,
      this.feedbackText,
    ]);
  }

  private buildBlockColumns(W: number, H: number) {
    const cols = [
      { label: '100', color: '#c080ff', bgColor: 0x5a1888, x: W * 0.22, increment: () => this.addHundred() },
      { label: '10',  color: '#00e5ff', bgColor: 0x007a99, x: W * 0.5,  increment: () => this.addTen() },
      { label: '1',   color: '#ffd700', bgColor: 0xcc8800, x: W * 0.78, increment: () => this.addOne() },
    ];

    cols.forEach(({ label, color, bgColor, x, increment }) => {
      // Column background
      const colBg = this.scene.add.graphics();
      colBg.fillStyle(bgColor, 0.15);
      colBg.fillRoundedRect(x - 55, H * 0.5, 110, H * 0.2, 10);
      colBg.lineStyle(1, bgColor, 0.3);
      colBg.strokeRoundedRect(x - 55, H * 0.5, 110, H * 0.2, 10);

      // Big block image/graphic
      const blockGfx = this.scene.add.graphics();
      this.drawBlock(blockGfx, x, H * 0.585, label);

      // Label
      const labelTxt = this.scene.add.text(x, H * 0.52, label, {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '18px', fontStyle: 'bold', color,
        stroke: '#000', strokeThickness: 2,
      }).setOrigin(0.5, 0.5);

      // +/- buttons
      const addBtn = this.scene.add.text(x + 30, H * 0.64, '+', {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '26px', fontStyle: 'bold', color: '#ffffff',
        backgroundColor: '#1a1a3e', padding: { x: 8, y: 2 },
      }).setOrigin(0.5, 0.5).setInteractive({ cursor: 'pointer' });
      addBtn.on('pointerdown', () => { increment(); audio.click(); });
      addBtn.on('pointerover', () => addBtn.setStyle({ color: CS.gold }));
      addBtn.on('pointerout', () => addBtn.setStyle({ color: '#ffffff' }));

      const subBtn = this.scene.add.text(x - 30, H * 0.64, '−', {
        fontFamily: '"Nunito", Arial, sans-serif',
        fontSize: '26px', fontStyle: 'bold', color: '#ffffff',
        backgroundColor: '#1a1a3e', padding: { x: 8, y: 2 },
      }).setOrigin(0.5, 0.5).setInteractive({ cursor: 'pointer' });
      subBtn.on('pointerdown', () => {
        if (label === '100') this.removeHundred();
        else if (label === '10') this.removeTen();
        else this.removeOne();
        audio.click();
      });
      subBtn.on('pointerover', () => subBtn.setStyle({ color: '#ff8c00' }));
      subBtn.on('pointerout', () => subBtn.setStyle({ color: '#ffffff' }));

      this.container.add([colBg, blockGfx, labelTxt, addBtn, subBtn]);
    });
  }

  private drawBlock(g: Phaser.GameObjects.Graphics, x: number, y: number, label: string) {
    const cx = x - 20, cy = y - 20;
    if (label === '100') {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0x9b4dff, 0.07 * i); g.fillRoundedRect(cx - i * 2, cy - i * 2, 40 + i * 4, 40 + i * 4, 8);
      }
      g.fillStyle(0x5a1888, 1); g.fillRoundedRect(cx, cy, 40, 40, 7);
      g.fillStyle(0x7b2fff, 0.9); g.fillRoundedRect(cx + 2, cy + 2, 36, 18, 5);
      g.fillStyle(0xffffff, 0.12); g.fillRoundedRect(cx + 4, cy + 4, 18, 8, 3);
    } else if (label === '10') {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0x00e5ff, 0.07 * i); g.fillRoundedRect(cx - i * 2 + 6, cy - i * 2, 28 + i * 4, 40 + i * 4, 5);
      }
      g.fillStyle(0x007a99, 1); g.fillRoundedRect(cx + 6, cy, 28, 40, 5);
      g.fillStyle(0x00bcd4, 0.9); g.fillRoundedRect(cx + 8, cy + 2, 24, 16, 4);
      g.fillStyle(0xffffff, 0.12); g.fillRoundedRect(cx + 10, cy + 4, 12, 7, 2);
    } else {
      for (let i = 2; i >= 1; i--) {
        g.fillStyle(0xffd700, 0.1 * i); g.fillRoundedRect(cx - i * 2 + 8, cy - i * 2 + 8, 24 + i * 4, 24 + i * 4, 4);
      }
      g.fillStyle(0xcc8800, 1); g.fillRoundedRect(cx + 8, cy + 8, 24, 24, 4);
      g.fillStyle(0xffc107, 0.9); g.fillRoundedRect(cx + 10, cy + 10, 20, 11, 3);
      g.fillStyle(0xffffff, 0.15); g.fillRoundedRect(cx + 12, cy + 12, 9, 5, 2);
    }
  }

  private drawMachine(cx: number, cy: number, fillPct: number) {
    const g = this.machineGfx;
    g.clear();
    // Machine housing
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(0x00e5ff, 0.04 * i * fillPct);
      g.fillRoundedRect(cx - 80 - i * 4, cy - 28 - i * 3, 160 + i * 8, 56 + i * 6, 12);
    }
    g.fillStyle(0x0d0230, 1); g.fillRoundedRect(cx - 80, cy - 28, 160, 56, 10);
    g.fillStyle(0x1a0a3e, 1); g.fillRoundedRect(cx - 76, cy - 24, 152, 22, 8);
    if (fillPct > 0) {
      const col = fillPct < 0.5 ? 0xff8c00 : fillPct < 0.9 ? 0xffcc00 : 0x00e5ff;
      g.fillStyle(col, 0.9); g.fillRoundedRect(cx - 74, cy - 22, 148 * fillPct, 18, 6);
      g.fillStyle(0xffffff, 0.2); g.fillRoundedRect(cx - 72, cy - 20, 100 * fillPct, 7, 4);
    }
    g.lineStyle(1.5, fillPct > 0.9 ? 0x00e5ff : 0x3d2060, 0.8);
    g.strokeRoundedRect(cx - 80, cy - 28, 160, 56, 10);
    // Decorative rivets
    [[-70, -18], [62, -18], [-70, 18], [62, 18]].forEach(([dx, dy]) => {
      g.fillStyle(0x3d2060, 1); g.fillCircle(cx + dx, cy + dy, 4);
      g.fillStyle(0x5a3090, 0.6); g.fillCircle(cx + dx - 1, cy + dy - 1, 2);
    });
  }

  private updateDisplay() {
    const total = this.currentH * 100 + this.currentT * 10 + this.currentO;
    const target = this.question.target;
    const pct = Math.min(1, total / target);

    this.totalDisplay.setText(`${total}`);
    const col = total === target ? '#00e676' : total > target ? '#ff5252' : '#00e5ff';
    this.totalDisplay.setStyle({ color: col });

    this.drawMachine(this.W * 0.5, this.H * 0.38, pct);

    this.counterH.setText(`Hundreds: ${this.currentH}`);
    this.counterT.setText(`Tens: ${this.currentT}`);
    this.counterO.setText(`Ones: ${this.currentO}`);

    // Energy bar
    const g = this.energyBarFill;
    g.clear();
    if (pct > 0) {
      const barCol = total === target ? 0x00e676 : total > target ? 0xff5252 : 0xff8c00;
      g.fillStyle(barCol, 1);
      g.fillRoundedRect(this.W * 0.25 + 1, this.H * 0.49 + 1, (this.W * 0.5 - 2) * pct, 12, 5);
      g.fillStyle(0xffffff, 0.2);
      g.fillRoundedRect(this.W * 0.25 + 3, this.H * 0.49 + 2, (this.W * 0.5 - 6) * pct * 0.6, 5, 3);
    }

    // Sparkle on match
    if (total === target) {
      audio.sparkle();
    }
  }

  private addHundred() { if (this.currentH < 9) { this.currentH++; this.updateDisplay(); } }
  private removehundred() { if (this.currentH > 0) { this.currentH--; this.updateDisplay(); } }
  private removeHundred() { this.removehundred(); }
  private addTen()     { if (this.currentT < 9) { this.currentT++; this.updateDisplay(); } }
  private removeTen()  { if (this.currentT > 0) { this.currentT--; this.updateDisplay(); } }
  private addOne()     { if (this.currentO < 9) { this.currentO++; this.updateDisplay(); } }
  private removeOne()  { if (this.currentO > 0) { this.currentO--; this.updateDisplay(); } }

  private clearAll() {
    this.currentH = 0; this.currentT = 0; this.currentO = 0;
    this.updateDisplay();
    this.feedbackText.setText('');
  }

  private showHint() {
    this.hintUsed = true;
    SaveManager.addXP(XP_HINT);
    const { hundreds, tens, ones } = this.question;
    this.feedbackText.setStyle({ color: '#ffeb3b' });
    this.feedbackText.setText(
      `Hint: ${this.question.target} = ${hundreds} hundred${hundreds !== 1 ? 's' : ''} + ${tens} ten${tens !== 1 ? 's' : ''} + ${ones} one${ones !== 1 ? 's' : ''}`,
    );
    audio.hint();
    this.scene.time.delayedCall(4000, () => {
      if (this.feedbackText.text.startsWith('Hint')) this.feedbackText.setText('');
    });
  }

  private checkAnswer() {
    this.attempts++;
    const total = this.currentH * 100 + this.currentT * 10 + this.currentO;
    const target = this.question.target;

    if (total === target) {
      // Correct!
      SaveManager.recordAnswer(this.question.skillId, true);
      SaveManager.addXP(this.hintUsed ? XP_HINT : XP_CORRECT);
      audio.correct();
      this.feedbackText.setStyle({ color: '#00e676' });
      this.feedbackText.setText('✓ Perfect! The crystals are energized!');

      // Success burst
      try {
        const emitter = this.scene.add.particles(this.W * 0.5, this.H * 0.38, 'particle_gold', {
          speed: { min: 80, max: 200 },
          angle: { min: 0, max: 360 },
          scale: { start: 1, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan: 800,
          quantity: 20,
          tint: [0xffd700, 0xff8c00, 0x00e5ff],
        });
        this.scene.time.delayedCall(300, () => emitter.stop());
        this.scene.time.delayedCall(1200, () => emitter.destroy());
      } catch { /* ok */ }

      this.scene.time.delayedCall(1400, () => {
        this.destroy();
        this.onComplete(true);
      });
    } else {
      // Wrong
      SaveManager.recordAnswer(this.question.skillId, false);
      audio.wrong();
      const diff = total - target;
      const direction = diff > 0 ? 'too much!' : 'not enough yet!';
      const specific = total > target
        ? `You have ${Math.abs(diff)} too many.`
        : `You need ${Math.abs(diff)} more.`;

      this.feedbackText.setStyle({ color: '#ff8888' });
      this.feedbackText.setText(`That's ${direction} ${specific}`);

      // Shake the panel
      this.scene.tweens.add({
        targets: this.container,
        x: -8,
        duration: 60,
        yoyo: true,
        repeat: 3,
        ease: 'Linear',
        onComplete: () => this.container.setX(0),
      });

      // Offer hint after 2 wrong attempts
      if (this.attempts >= 2 && !this.hintUsed) {
        this.scene.time.delayedCall(2000, () => {
          if (this.feedbackText.active) {
            this.feedbackText.setStyle({ color: '#ffeb3b' });
            this.feedbackText.setText('💡 Tap the Hint button if you need help!');
          }
        });
      }
    }
  }

  private makeButton(x: number, y: number, label: string, color: string, cb: () => void): Phaser.GameObjects.Text {
    const btn = this.scene.add.text(x, y, label, {
      fontFamily: '"Nunito", Arial, sans-serif',
      fontSize: '16px', fontStyle: 'bold',
      color,
      backgroundColor: '#0d0230',
      padding: { x: 18, y: 10 },
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setInteractive({ cursor: 'pointer' });
    btn.on('pointerover', () => {
      this.scene.tweens.add({ targets: btn, scaleX: 1.06, scaleY: 1.06, duration: 100 });
      audio.click();
    });
    btn.on('pointerout', () => this.scene.tweens.add({ targets: btn, scaleX: 1, scaleY: 1, duration: 100 }));
    btn.on('pointerdown', cb);
    return btn;
  }

  destroy() {
    this.container.destroy();
  }
}
